import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy, usePagination } from "react-table";
import { useExportData } from "react-table-plugins";
import { COLUMNS } from "./columns";
import ColumnFilter from "./ColumnFilter";
import { MdDownload } from "react-icons/md";
import { CSVLink } from "react-csv";

import "./userTable.css";
import DemographicData from "./DemographicData";
import { timeConverter } from "./TableHelper";

function FilteringTable({
  initialUserData: allUsers,
  userData,
  setUserData,
  activeUsers,
  downloadableData,
}) {
  const columns = useMemo(() => COLUMNS, []);
  const data = useMemo(() => userData, [userData]);
  const [showComplementary, setShowComplementary] = useState(false);

  const [showDemographics, setShowDemographics] = useState(false);

  const defaultColumn = useMemo(() => {
    return {
      Filter: ColumnFilter,
    };
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    gotoPage,
    pageCount,
    setPageSize,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFilters,
    useSortBy,
    useExportData,
    usePagination
  );

  const { pageIndex, pageSize } = state;

  useEffect(() => {
    if (showComplementary) setUserData(allUsers);
    else
      setUserData(
        userData.filter((u) => {
          return (
            u.status !== null && u.status.toLowerCase() !== "complementary"
          );
        })
      );
  }, [showComplementary]);

  return (
    <>
      <div className="controls-and-stats">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* <span>
              Total users (excl. complementary):{" "}
              {
                allUsers.filter((u) => {
                  return u.status !== "Complementary" && u.status !== null;
                }).length
              }
            </span> */}
            <span>Total active users: {activeUsers}</span>
          </div>
          <div style={{ position: "relative" }}>
            <button
              className="view-demographics-button"
              onClick={() => {
                setShowDemographics((s) => !s);
              }}
            >
              <span>{showDemographics ? "Hide" : "View"} demographic data</span>
            </button>
            <DemographicData
              userData={allUsers}
              visible={showDemographics}
              setVisible={setShowDemographics}
            />
          </div>
          <CSVLink
            className="download-csv-button"
            data={downloadableData}
            filename={`GbM user data (exported on ${timeConverter(
              new Date(),
              false
            )})`}
          >
            <MdDownload size={20} />
            <span style={{ marginLeft: "0.25rem" }}>Download .csv</span>
          </CSVLink>
        </div>
        <div className="complementary-toggle">
          <input
            type="checkbox"
            id="complementary"
            checked={showComplementary}
            onChange={(e) => {
              setShowComplementary(e.target.checked);
            }}
          />
          <label for="complementary"> include complementary</label>
        </div>
      </div>
      <div className="user-table-container">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              return (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    return (
                      <th
                        {...column.getHeaderProps()}
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                      >
                        {column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? "\u25bc" // unicode for down arrow
                              : "\u25b2" // unicode for up arrow
                            : null}
                        </span>
                        <div>{column.canFilter && column.render("Filter")}</div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>
                        {cell.render("Cell", {
                          setUserData: () => {
                            setUserData();
                          },
                          userData: userData,
                          index: i,
                        })}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="table-pagination-controls">
        <div className="top-controls-container">
          <span>
            Go to page{" "}
            <input
              className="table-manual-page-navigation"
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const pageNumber = e.target.value
                  ? Number(e.target.value) - 1
                  : 0;
                gotoPage(pageNumber);
              }}
            />
          </span>
          <span>
            Rows per page:{" "}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 25, 50, 100].map((size) => {
                return (
                  <option key={size} value={size}>
                    {size}
                  </option>
                );
              })}
            </select>
          </span>
        </div>
        <div className="page-button-container">
          <button
            onClick={() => {
              gotoPage(0);
            }}
            disabled={!canPreviousPage}
          >
            {"<<"}
          </button>
          <button
            onClick={() => {
              previousPage();
            }}
            disabled={!canPreviousPage}
          >
            {"<"}
          </button>
          <span className="table-current-page-label">
            Page {pageIndex + 1} of {pageOptions.length}
          </span>
          <button
            onClick={() => {
              nextPage();
            }}
            disabled={!canNextPage}
          >
            {">"}
          </button>
          <button
            onClick={() => {
              gotoPage(pageCount - 1);
            }}
            disabled={!canNextPage}
          >
            {">>"}
          </button>
        </div>
      </div>
    </>
  );
}

export default FilteringTable;
